<template>
<span>
  <el-tooltip v-if="!hideTrigger && edit"
    content="Edit image"
    effect="light"
    :open-delay="300"
    placement="top"
  >
                <base-button :size="size"
                  @click="beginEdit(edited_image)" icon class="btn btn-link" type="warning">
                  <i class="tim-icons icon-pencil"></i>
                </base-button>
              </el-tooltip>
  <base-button v-else-if="!hideTrigger" :size="size" type="primary" @click="uploadNew()">{{text}}</base-button>
  <modal :show.sync="open"
         body-classes="p-0"
         :closable="!image"
         modal-classes="modal-dialog-centered">
    <card type="secondary"
          header-classes="bg-white"
          class="border-0 mb-0">

      <div class="row" >
        <div class="col">
          <h4 class="card-title mt-2">Upload image</h4>
        </div>
        <div class="col text-right" v-if="image">
          <base-button class="btn btn-link text-primary card-title" link v-on:click="clearImage()">Upload a new image</base-button>
        </div>
      </div>

      <div v-if="fetching_original" class="mb-4">
        <span class="skel d-block mb-3" style="height: 200px; border-radius: 5px;"></span>
        <div class="d-flex mb-3">
          <span class="skel skel--badge mr-2" style="width: 110px; height: 26px; border-radius: 13px;"></span>
          <span class="skel skel--badge" style="width: 90px; height: 26px; border-radius: 13px;"></span>
        </div>
        <span class="skel skel--line-sm d-block mb-2"></span>
        <span class="skel skel--text d-block" style="height: 48px; border-radius: 0.4285rem;"></span>
      </div>

      <div id="image-container"
           v-if="image && activeMode === 'free' && !fetching_original" style="border-radius: 5px; width: 100%; height: 250px; overflow: hidden; position: relative;"
           :style="{'background-color': image_background_color}"
           v-on:drop="dropHandler"
           v-on:dragover="dragOverHandler">
        <img id="img" draggable="true" v-on:dragstart="dragStartHandler"
             :style="{'transform': 'translate(0%, 0%) scale(' + image_size*0.01 + ')', 'object-fit': object_fit, 'height': '100%'}"
             v-if="image" class="card-img"
             style="position: absolute; cursor: move; transform-origin: center center; will-change: transform"
             slot="image" :src="image"  alt="Card image cap"/>

      </div>

      <div v-if="image && activeMode === 'crop'" class="crop-frame">
        <vue-cropper ref="cropper"
                     :key="image + aspect"
                     :src="image"
                     :aspect-ratio="aspectRatio"
                     :view-mode="1"
                     drag-mode="move"
                     :auto-crop-area="0.9"
                     :background="false"
                     :responsive="true" />
      </div>

      <div v-if="image && activeMode === 'fit'" class="fit-frame"
           :style="{ aspectRatio: aspect === 'portrait' ? '3 / 4' : '16 / 9', backgroundColor: image_background_color }">
        <img :src="image" :style="{ maxWidth: image_size + '%', maxHeight: image_size + '%' }" alt="Fit preview" />
      </div>

      <div class="row justify-content-center">
        <div class="col text-center">
          <el-upload v-if="!image"
                     class="upload-demo mt-5 mb-5"
                     drag :on-change="previewFile"
                     :auto-upload="false"
                     accept="image/png,image/jpeg,image/webp"
                     action=""
          >
            <i class="tim-icons el-icon-upload"></i>
            <div class="el-upload__text">
              Drop an image here or <em class="text-primary">click to upload</em>
            </div>
          </el-upload>
        </div>
      </div>

      <div class="row mt-3 mb-1" v-if="composeModes && image && file">
        <div class="col-auto">
          <label class="d-block mb-1">Composition</label>
          <segmented-toggle size="sm" v-model="compose_mode" :options="composeModeOptions" />
        </div>
        <div class="col-auto mt-2" v-if="compose_mode !== 'free'">
          <label class="d-block mb-1">Orientation</label>
          <segmented-toggle size="sm" v-model="aspect" :options="aspectOptions" />
        </div>
      </div>

      <div class="row mt-2" v-if="image && showFit && !fetching_original">
        <div class="col">
          <base-textarea label="Description" v-model="description" :rows="2" placeholder="Description" />
        </div>
      </div>

      <div class="row mt-3" v-if="image && !fetching_original">
        <div class="col" v-if="showBackgroundColor && activeMode !== 'crop'">
          <base-input label="Background color">
            <el-color-picker v-model="image_background_color" show-alpha></el-color-picker>
          </base-input>
        </div>
        <div class="col" v-if="showFit && activeMode === 'free'">
          <base-input label="Image fit">
            <el-select
              v-model="object_fit"
              class="select-primary"
            >
              <el-option
                v-for="option in fitOptions"
                class="select-primary"
                :label="option"
                :value="option"
                :key="option"
              ></el-option>
            </el-select>
          </base-input>
        </div>
        <div class="col" v-if="show_slider && activeMode !== 'crop'">
          <base-input label="Image scale">
            <el-slider :show-tooltip="false" v-model="image_size" />
          </base-input>
        </div>
      </div>


      <div class="row text-center">
        <div class="col">
          <base-button type="secondary" :disabled="loading" @click="cancelModal()">Cancel</base-button>
          <base-button class="ml-3" type="primary" :loading="loading" :disabled="fetching_original" @click="handleSave">
            {{ loading ? 'Saving' : 'Save' }}
          </base-button>
        </div>

      </div>

    </card>
  </modal>
</span>
</template>

<script>
import {Upload, Slider, ColorPicker } from 'element-ui';
import {API_LOCATION} from "@/api-config";
import axios from "axios";
import {Auth} from "aws-amplify";
import swal from "sweetalert2";
import 'cropperjs/dist/cropper.css';
export default {
  name: "ImageEditor",
  components: {
    [Upload.name]: Upload,
    [Slider.name]: Slider,
    [ColorPicker.name]: ColorPicker,
    VueCropper: () => import('vue-cropperjs'),
  },
  props:{
    edit: Boolean,
    edited_image: Object,
    // When true, the component renders no trigger button — the parent drives it
    // via a ref (uploadNew()/beginEdit()) from its own custom control.
    hideTrigger: Boolean,
    text: {
      default: "Upload new image",
    },
    size: '',
    show_slider: {
      type: Boolean,
      default: true
    },
    showFit: {
      type: Boolean,
      default: true
    },
    showBackgroundColor: {
      type: Boolean,
      default: true
    },
    showDescription: {
      type: Boolean,
      default: true
    },
    // Opt-in: adds crop-to-fill and fit-with-background baked compose modes.
    // Off by default so the other consumers keep the legacy modal untouched.
    composeModes: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      file: '',
      uploadUrl: '',
      original_url: '',
      fetching_original: false,
      // True when `file` was hydrated from an already-uploaded S3 url during
      // edit; saveFile skips the redundant re-upload in that case.
      file_is_remote: false,
      image: '',
      image_id: '',
      image_size: 100,
      image_background_color: '#e8e7ec',
      isOpen: this.open,
      loading: false,
      open: false,
      draggedElement: null,
      position: {
        x: 0,
        y: 0
      },
      description: '',
      object_fit: 'cover',
      fitOptions: ['cover', 'fill', 'contain', 'scale-down', 'none'],
      compose_mode: 'free',   // 'free' | 'crop' | 'fit'
      aspect: 'landscape',    // 'landscape' | 'portrait'
      composeModeOptions: [
        { value: 'crop', label: 'Crop to fill' },
        { value: 'fit', label: 'Fit with background' },
        { value: 'free', label: 'Free position' }
      ],
      aspectOptions: [
        { value: 'landscape', label: 'Landscape' },
        { value: 'portrait', label: 'Portrait' }
      ]
    };
  },
  computed: {
    // New modes require a fresh local file (canvas export of a remote S3 url
    // would taint the canvas). Editing an existing image without replacing the
    // file therefore always behaves as legacy free position.
    activeMode() {
      return this.composeModes && this.file ? this.compose_mode : 'free';
    },
    aspectRatio() {
      return this.aspect === 'portrait' ? 3 / 4 : 16 / 9;
    },
    exportDims() {
      return this.aspect === 'portrait'
        ? { w: 1080, h: 1440 }
        : { w: 1600, h: 900 };
    }
  },
  methods: {
    async beginEdit(image){
      this.compose_mode = 'free'
      this.original_url = ''
      this.file = ''
      this.object_fit = image.object_fit
      this.position.x = image.x
      this.position.y = image.y
      this.description = image.description
      this.image_background_color = image.background_color
      this.image_size = image.scale
      this.image = image.url
      this.uploadUrl = image.url
      this.image_id = image.id
      this.open = true
      // Hydrate the source file so all compose modes are available on edit.
      // Baked images reopen on their original upload (lossless re-crop);
      // legacy images are assumed free position and use their current url as
      // the source. Fetching as a blob avoids canvas tainting; if the fetch
      // fails (e.g. CORS), fall back to the plain metadata view.
      if (this.composeModes && image.url) {
        const isBaked = image.original_url && (image.compose_mode === 'crop' || image.compose_mode === 'fit')
        const sourceUrl = isBaked ? image.original_url : image.url
        this.fetching_original = true
        try {
          const res = await axios.get(sourceUrl, { responseType: 'blob' })
          const name = sourceUrl.split('/').pop() || 'image'
          this.file = new File([res.data], name, { type: res.data.type })
          this.file_is_remote = true
          this.image = URL.createObjectURL(res.data)
          this.original_url = sourceUrl
          this.compose_mode = isBaked ? image.compose_mode : 'free'
          const probe = new Image()
          probe.onload = () => {
            this.aspect = probe.naturalHeight > probe.naturalWidth ? 'portrait' : 'landscape'
          }
          probe.src = image.url
        } catch (err) {
          console.error(err)
        } finally {
          this.fetching_original = false
        }
      }
    },
    dropHandler(event){
      event.preventDefault();

      if (this.draggedElement) {
        // Get the mouse coordinates
        const mouseX = event.clientX;
        const mouseY = event.clientY;

        // Get the position of the parent container
        const containerRect = document.getElementById('image-container').getBoundingClientRect();

        // Calculate the new position relative to the container
        const newPositionX = mouseX - containerRect.left - this.draggedElement.width / 2;
        const newPositionY = mouseY - containerRect.top - this.draggedElement.height / 2;

        // Set the new position
        this.draggedElement.style.position = 'absolute';
        this.draggedElement.style.left = newPositionX + 'px';
        this.draggedElement.style.top = newPositionY + 'px';
        this.position = { x: newPositionX, y: newPositionY}

        // Reset draggedElement to null
        this.draggedElement = null;
      }
    },
    dragStartHandler(event){
      this.draggedElement = event.target;
    },
    dragOverHandler(event){
      event.preventDefault();
    },
    cancelModal(){
      if (this.image && this.file) {
        swal({
          title: 'Discard image?',
          text: 'The uploaded image and your edits will be lost.',
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Discard',
          cancelButtonText: 'Keep editing'
        }).then((response) => {
          if (response.value === true) {
            this.clearImage()
            this.open = false
            this.$emit('closed');
          }
        })
        return
      }
      this.clearImage()
      this.open = false
      this.$emit('closed');
    },
    uploadNew(){
      this.open = true
      this.clearImageFully()
      if (this.composeModes) this.compose_mode = 'crop'
      this.$emit('clickUpload');
    },
    clearImageFully(){
      this.image = ''
      this.file = ''
      this.file_is_remote = false
      this.uploadUrl = ''
      this.original_url = ''
      this.image_id = ''
      this.image_size = 100,
      this.image_background_color = '#e8e7ec'
      this.position = {
        x: 0,
        y: 0
      }
      this.description = ''
      this.object_fit = 'cover'
      this.compose_mode = this.composeModes ? 'crop' : 'free'
      this.aspect = 'landscape'
    },
    clearImage(){
      this.image = ''
      this.file = ''
      this.file_is_remote = false
      this.original_url = ''
    },
    previewFile(file){
      const raw = file.raw
      const allowedTypes = ['image/png', 'image/jpeg', 'image/webp']
      if (!raw || !allowedTypes.includes(raw.type)) {
        this.$notify({ type: 'danger', message: 'Please upload a png, jpeg, or webp image.' })
        return
      }
      if (raw.size > 10 * 1024 * 1024) {
        this.$notify({ type: 'danger', message: 'That image is too large. The maximum size is 10 MB.' })
        return
      }
      this.file = raw
      this.file_is_remote = false
      this.original_url = ''
      this.image = URL.createObjectURL(raw);
      if (this.composeModes) {
        const probe = new Image();
        probe.onload = () => {
          this.aspect = probe.naturalHeight > probe.naturalWidth ? 'portrait' : 'landscape';
        };
        probe.src = this.image;
      }
      this.$emit('change', file);
    },
    getAuthToken() {
      return new Promise ( function(resolve) {
        Auth.currentSession()
          .then(data =>{
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    uploadFile (file) {
      let url = this.uploadUrl
      let config = { headers: {
          'Content-Type': file.type
        }}

      let payload = file
      return new Promise ( function(resolve, reject) {
        axios.put(url, payload, config)
          .then((res) => {
            resolve(res)
          })
          .catch((err) => {
            console.error(err)
            reject(err)
          })
      })
    },
    async getSignedURL (file) {
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let url = API_LOCATION + 'upload'

      let payload = {
        filePath: file.name,
        contentType: file.type
      }

      return new Promise ( function(resolve, reject) {
        axios.post(url, payload, config)
          .then((res) => {
            resolve(res.data)
          })
          .catch((err) => {
            console.error(err)
            reject(err)
          })
      })
    },
    async saveFile() {
      if(this.file != '' && !this.file_is_remote){
        this.uploadUrl = await this.getSignedURL(this.file)
        let uploaded = await this.uploadFile(this.file)
        //Get the image URL
        let imgUrl = this.uploadUrl.split("?")
        this.uploadUrl = imgUrl[0]
      }
    },
    canvasToFile(canvas, name, type, quality) {
      return new Promise(resolve => {
        canvas.toBlob(blob => resolve(new File([blob], name, { type })), type, quality);
      });
    },
    loadImageEl(src) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
      });
    },
    async bakeFitCanvas() {
      const img = await this.loadImageEl(this.image);
      const { w, h } = this.exportDims;
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = this.image_background_color || '#e8e7ec';
      ctx.fillRect(0, 0, w, h);
      const containScale = Math.min(w / img.naturalWidth, h / img.naturalHeight);
      const s = containScale * (this.image_size / 100);
      const dw = img.naturalWidth * s;
      const dh = img.naturalHeight * s;
      ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
      return canvas;
    },
    async handleSave() {
      try {
        this.loading = true
        if (this.activeMode !== 'free') {
          const baseName = ((this.file && this.file.name) || 'image').replace(/\.[^.]+$/, '');
          // Upload the untouched source once so the bake can be redone later.
          // Re-edits arrive with original_url already set; don't re-upload.
          let originalUrl = this.original_url
          if (!originalUrl) {
            const srcFile = this.file
            this.uploadUrl = await this.getSignedURL(srcFile)
            await this.uploadFile(srcFile)
            originalUrl = this.uploadUrl.split('?')[0]
          }
          let outFile;
          if (this.activeMode === 'crop') {
            const canvas = this.$refs.cropper.getCroppedCanvas({
              width: this.exportDims.w,
              height: this.exportDims.h,
              imageSmoothingQuality: 'high',
              fillColor: '#ffffff'
            });
            outFile = await this.canvasToFile(canvas, baseName + '-crop.jpg', 'image/jpeg', 0.85);
          } else {
            const canvas = await this.bakeFitCanvas();
            outFile = await this.canvasToFile(canvas, baseName + '-fit.png', 'image/png');
          }
          this.file = outFile;
          this.file_is_remote = false;
          await this.saveFile();
          this.open = false;
          this.$emit('saveImage', {
            file: this.image,
            scale: 100,
            background_color: this.activeMode === 'fit' ? this.image_background_color : '#e8e7ec',
            description: this.description,
            url: this.uploadUrl,
            original_url: originalUrl,
            compose_mode: this.activeMode,
            x: 0,
            y: 0,
            object_fit: 'cover',
            image_id: this.image_id
          });
          return;
        }
        // Free position edits compose the hydrated source file; it is already
        // in S3, so point url at it rather than re-uploading a copy. Covers
        // both legacy edits (source == current url, no-op) and a baked image
        // switched back to free position (url becomes the original).
        if (this.file_is_remote && this.original_url) {
          this.uploadUrl = this.original_url
        }
        await this.saveFile()
        this.open = false
        let image = {
          file : this.image,
          scale: this.image_size,
          background_color: this.image_background_color,
          description: this.description,
          url: this.uploadUrl,
          original_url: null,
          compose_mode: 'free',
          x: this.position.x,
          y: this.position.y,
          object_fit: this.object_fit,
          image_id: this.image_id
        }
        this.$emit('saveImage', image);
      } catch (err) {
        console.error(err)
        this.$notify({ type: 'danger', message: 'The image upload failed. Please try again.' })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.crop-frame {
  border-radius: 5px;
  overflow: hidden;
}
/* Cropperjs sizes its canvas from the source img's display box, so capping
   the img height here bounds the whole cropper with no dead space below. */
.crop-frame img {
  display: block;
  max-width: 100%;
  max-height: 320px;
  margin: 0 auto;
}
/* Cropper chrome is near-invisible on the dark theme by default; use the
   app primary so the crop box reads clearly on any image in both themes. */
.crop-frame ::v-deep .cropper-view-box {
  outline: 2px solid #e14eca;
  outline-offset: -1px;
}
.crop-frame ::v-deep .cropper-line {
  background-color: #e14eca;
}
.crop-frame ::v-deep .cropper-point {
  background-color: #e14eca;
  width: 8px;
  height: 8px;
  opacity: 0.9;
}
.crop-frame ::v-deep .cropper-dashed {
  border-color: rgba(255, 255, 255, 0.5);
}
.crop-frame ::v-deep .cropper-modal {
  background-color: rgba(0, 0, 0, 0.6);
}
.fit-frame {
  /* Height-constrained like .crop-frame; width derives from the inline
     aspect-ratio so portrait stays compact instead of filling modal width. */
  height: 320px;
  max-width: 100%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  overflow: hidden;
}
.fit-frame img {
  object-fit: contain;
}
</style>
